import React from 'react';
import { useEffect, useState } from 'react';
import { useGetLiveActivitiesQuery } from 'app/api/backendApiSlice';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Typography } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import getDuration from 'utils/getDuration';
import MLDialog from 'components/common/Dialog/Dialog';
import MLLoader from 'components/common/Loader/Loader';
import NoDataFound from 'components/common/NoDataFound/NoDataFound';
import ActivityDashboard from '../ActivityDashboard/ActivityDashboard';
import StatusFilterCards from './StatusFilterCards';
import { getStatusStyle } from './statusStyles';

const generateRows = (data) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const now = dayjs();
  const startDate = dayjs.utc(data.activitystartdate);
  const endDate = dayjs.utc(data.activityenddate ?? now);

  const duration = getDuration({
    start: startDate,
    end: endDate,
    expected: data.expectedenddate,
  });

  return {
    badgeText: duration.isOverdue
      ? duration.isLate
        ? 'Late'
        : 'Overdue'
      : 'On Track',
    badgeStatus: duration.isOverdue
      ? duration.isLate
        ? 'warning'
        : 'error'
      : 'good',
    progressBar: Math.min(
      (duration.totalMins / data.expecteddurationminutes) * 100,
      100
    ),
    fullName: data.fullname,
    trailName: data.trailname,
    participants: data.additionalparticipants + 1, // add 1 for the main participant
    startDatetime: dayjs
      .utc(data.activitystartdate)
      .local()
      .format('D MMMM h:mm A'),
    currentDuration: duration.durationText,
    rowClickVars: {
      activityId: data.activityid,
      userEmail: data.emailaddress,
    },
  };
};

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const DurationRing = ({ percent, color }) => {
  const clamped = Math.min(Math.max(percent, 0), 100);
  const offset = CIRCUMFERENCE - (clamped / 100) * CIRCUMFERENCE;

  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
      <circle
        cx="28"
        cy="28"
        r={RADIUS}
        fill="none"
        stroke="var(--Grey-20)"
        strokeWidth="5"
      />
      <circle
        cx="28"
        cy="28"
        r={RADIUS}
        fill="none"
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        transform="rotate(-90 28 28)"
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
    </svg>
  );
};

// Activities get closed from outside this app instance (a walker's own
// response link, a different Safety Monitor's session, the scheduled
// Welfare Check trigger) with no way to invalidate this page's RTK Query
// cache when that happens — so this view polls instead of trusting the
// cache to reflect the true current state.
const LIVE_ACTIVITIES_POLL_INTERVAL_MS = 15000;

const LiveActivities = () => {
  // Paused while an activity's modal is open — a background refetch here
  // re-renders the whole page, including the open modal, disrupting anyone
  // mid-scroll through its comments or contacts.
  const [showActivityDash, setShowActivityDash] = useState(false);

  const { data = [], isLoading, error } = useGetLiveActivitiesQuery(undefined, {
    pollingInterval: showActivityDash ? 0 : LIVE_ACTIVITIES_POLL_INTERVAL_MS,
    refetchOnMountOrArgChange: true,
  });

  const [activities, setActivities] = useState([]);

  useEffect(() => {
    if (data) {
      setActivities([...data]);
    }
  }, [data]);

  const rows = activities
    ?.sort((a, b) => a.activitystartdate.localeCompare(b.activitystartdate))
    ?.map((activity) => generateRows(activity));

  const [statusFilter, setStatusFilter] = useState('all');
  const filteredRows =
    statusFilter === 'all'
      ? rows
      : rows.filter((row) => row.badgeStatus === statusFilter);

  const [activityId, setActivityId] = useState(null);
  const [activityEmail, setActivityEmail] = useState(null);

  const handleActivityClick = (vars) => {
    setActivityId(vars.activityId);
    setActivityEmail(vars.userEmail);
    setShowActivityDash(true);
  };

  if (error) return <div>Error loading activities</div>;

  return (
    <div className="backend-home p-4">
      <Typography variant="h2" className="px-4" component="h1" gutterBottom>
        Currently Live Activities
      </Typography>
      <div className="part-wrap flex flex-col gap-5 px-4">
        <StatusFilterCards
          rows={rows}
          activeFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />

        {isLoading ? (
          <div className="p-10">
            <MLLoader />
          </div>
        ) : filteredRows.length === 0 ? (
          <NoDataFound
            message="No activities match this filter."
            classList="rounded-2xl"
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredRows.map((row) => {
              const status = getStatusStyle(row.badgeStatus);
              return (
                <button
                  type="button"
                  key={row.rowClickVars.activityId}
                  onClick={() => handleActivityClick(row.rowClickVars)}
                  className="flex flex-col gap-3 rounded-2xl border border-[var(--ts-border)] bg-[var(--ts-surface)] p-5 text-left shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Typography className="!font-bold !truncate">
                        {row.fullName}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="!truncate text-[var(--Grey-70)]"
                      >
                        {row.trailName}
                      </Typography>
                    </div>
                    <DurationRing percent={row.progressBar} color={status.color} />
                  </div>

                  <div
                    className="w-fit rounded-full px-3 py-1 text-xs font-semibold"
                    style={{
                      backgroundColor: `${status.color}1f`,
                      color: status.color,
                    }}
                  >
                    {row.badgeText} · {row.currentDuration}
                  </div>

                  <div className="mt-1 flex items-center justify-between border-t border-[var(--ts-border)] pt-3 text-xs text-[var(--Grey-70)]">
                    <span>Started {row.startDatetime}</span>
                    <span className="flex items-center gap-1">
                      <PeopleAltIcon sx={{ fontSize: 14 }} />
                      {row.participants}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <MLDialog
        openDialog={showActivityDash}
        setOpenDialog={setShowActivityDash}
        classList={'activity-dashboard-dialog'}
      >
        <ActivityDashboard
          activityId={activityId}
          userEmail={activityEmail}
          handleClose={() => setShowActivityDash(false)}
        />
      </MLDialog>
    </div>
  );
};

export default LiveActivities;
